import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { Location } from "@visitproof/types";
import { LocationService } from "../services/LocationService";
import { useAccount } from "./AccountContext";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";

interface LocationContextProps {
  locations: Location[];
  selectedLocation: Location | null;
  setSelectedLocation: (locationId: string) => void;
  createLocation: (
    locationData: Omit<
      Location,
      "id" | "createdAt" | "updatedAt" | "accountId"
    >,
  ) => Promise<string>;
  updateLocation: (
    locationId: string,
    locationData: Partial<Omit<Location, "id" | "createdAt" | "updatedAt">>,
  ) => Promise<void>;
  deleteLocationById: (locationId: string) => Promise<void>;
}

const LocationContext = createContext<LocationContextProps | undefined>(
  undefined,
);

export const useLocation = (): LocationContextProps => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error("useLocation must be used within a LocationProvider");
  }
  return context;
};

export const LocationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { account } = useAccount();
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(
    null,
  );

  useEffect(() => {
    if (!account?.id) return;
    const docRef = query(
      collection(db, "locations"),
      where("accountId", "==", account.id),
    );
    const unsubscribe = onSnapshot(
      docRef,
      (docSnapshot) => {
        const locationList = docSnapshot.docs.map(
          (doc) => ({ id: doc.id, ...doc.data() }) as Location,
        );
        setLocations(
          locationList.sort((a, b) => a.name.localeCompare(b.name)),
        );
      },
      (err) => {
        console.error("Error fetching locations: ", err);
      },
    );
    return () => {
      unsubscribe();
    };
  }, [account]);

  const setSelectedLocationById = async (locationId: string) => {
    const locationData = await LocationService.getLocation(locationId);
    setSelectedLocation(locationData);
  };

  const createLocation = async (
    locationData: Omit<
      Location,
      "id" | "createdAt" | "updatedAt" | "accountId"
    >,
  ): Promise<string> => {
    if (!account?.id) {
      throw new Error("Account not available");
    }
    const newLocationId = await LocationService.createLocation({
      ...locationData,
      accountId: account.id,
    });
    return newLocationId;
  };

  const updateLocation = async (
    locationId: string,
    updatedData: Partial<Omit<Location, "id" | "createdAt" | "updatedAt">>,
  ) => {
    await LocationService.updateLocation(locationId, updatedData);
  };

  const deleteLocationById = async (locationId: string) => {
    await LocationService.deleteLocation(locationId);
  };

  return (
    <LocationContext.Provider
      value={{
        locations,
        selectedLocation,
        createLocation,
        updateLocation,
        deleteLocationById,
        setSelectedLocation: setSelectedLocationById,
      }}
    >
      {children}
    </LocationContext.Provider>
  );
};
